import { useId } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BPMSensitivityControlProps {
  threshold: number;
  onThresholdChange: (value: number) => void;
  compact?: boolean;
}

const getSensitivityLabel = (threshold: number): { label: string; color: string; description: string } => {
  if (threshold <= 5) {
    return {
      label: "Muito Alta",
      color: "bg-red-500/10 text-red-500 border-red-500/20",
      description: "Ajustes muito frequentes - muda a cada pequena variação"
    };
  }
  if (threshold <= 10) {
    return {
      label: "Alta",
      color: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      description: "Ajustes frequentes - responde rapidamente às mudanças"
    };
  }
  if (threshold <= 15) {
    return {
      label: "Média",
      color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      description: "Equilíbrio entre estabilidade e adaptação (recomendado)"
    };
  }
  if (threshold <= 20) {
    return {
      label: "Baixa",
      color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      description: "Ajustes menos frequentes - mais estável"
    };
  }
  return {
    label: "Muito Baixa",
    color: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    description: "Ajustes raros - máxima estabilidade"
  };
};

export const BPMSensitivityControl = ({
  threshold,
  onThresholdChange,
  compact = false
}: BPMSensitivityControlProps) => {
  const sensitivity = getSensitivityLabel(threshold);
  const compactLabelId = useId();

  if (compact) {
    return (
      <div className="space-y-3 p-3 rounded-lg bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label id={compactLabelId} className="text-sm font-medium">Sensibilidade de Ajuste</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger aria-label="Mais informações sobre sensibilidade de ajuste">
                  <Info className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    Controla quando a música muda automaticamente. 
                    Valores menores = ajustes mais frequentes.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Badge variant="outline" className={sensitivity.color}>
            {sensitivity.label}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <Slider
            aria-labelledby={compactLabelId}
            value={[threshold]}
            onValueChange={([value]) => onThresholdChange(value)}
            min={5}
            max={30}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>±{threshold} BPM</span>
            <span>{sensitivity.description}</span>
          </div>
        </div>
      </div>
    );
  }

  const labelId = useId();
  const descId = useId();

  return (
    <Card className="bg-card/95 backdrop-blur-md border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            Sensibilidade de Ajuste Automático
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger aria-label="Mais informações sobre sensibilidade de ajuste">
                  <Info className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    Define a diferença de BPM necessária entre sua frequência cardíaca 
                    e a música atual para que o sistema ajuste automaticamente a playlist.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <Badge variant="outline" className={sensitivity.color}>
            {sensitivity.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label id={labelId}>Threshold de BPM</Label>
            <span className="text-sm font-mono font-semibold text-primary">
              ±{threshold} BPM
            </span>
          </div>
          
          <Slider
            id="threshold-slider"
            aria-labelledby={labelId}
            aria-describedby={descId}
            value={[threshold]}
            onValueChange={([value]) => onThresholdChange(value)}
            min={5}
            max={30}
            step={5}
            className="w-full"
          />
          
          <div id={descId} className="flex justify-between text-xs text-muted-foreground">
            <span>5 BPM</span>
            <span>15 BPM</span>
            <span>30 BPM</span>
          </div>
        </div>

        <div className="p-3 rounded-lg bg-muted/50 space-y-2">
          <p className="text-xs font-medium">
            {sensitivity.description}
          </p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Valor menor: Ajustes mais frequentes, resposta rápida</p>
            <p>• Valor maior: Ajustes menos frequentes, mais estável</p>
            <p>• Recomendado: 10-15 BPM para melhor equilíbrio</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs" role="group" aria-label="Predefinições de sensibilidade">
          <button
            onClick={() => onThresholdChange(5)}
            className={`p-2 rounded border transition-colors ${
              threshold === 5 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-primary/50'
            }`}
            aria-label="Sensibilidade rápida: 5 BPM"
            aria-pressed={threshold === 5}
          >
            <div className="font-medium">Rápida</div>
            <div className="text-muted-foreground">5 BPM</div>
          </button>
          <button
            onClick={() => onThresholdChange(15)}
            className={`p-2 rounded border transition-colors ${
              threshold === 15 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-primary/50'
            }`}
            aria-label="Sensibilidade balanceada: 15 BPM"
            aria-pressed={threshold === 15}
          >
            <div className="font-medium">Balanceada</div>
            <div className="text-muted-foreground">15 BPM</div>
          </button>
          <button
            onClick={() => onThresholdChange(25)}
            className={`p-2 rounded border transition-colors ${
              threshold === 25 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-primary/50'
            }`}
            aria-label="Sensibilidade estável: 25 BPM"
            aria-pressed={threshold === 25}
          >
            <div className="font-medium">Estável</div>
            <div className="text-muted-foreground">25 BPM</div>
          </button>
        </div>
      </CardContent>
    </Card>
  );
};
